import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import Modal from "@/components/base/Modal";
import {
  useDebtors,
  adminCreateInstallments,
  adminMarkInstallmentPaid,
} from "@/hooks/usePayments";
import { useStudents } from "@/hooks/useStudents";

function formatPrice(value: number): string {
  return `₺${value.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors";
const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

export default function AdminBorclular() {
  const { data: debtors, loading, error, reload } = useDebtors();
  const students = useStudents();
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);

  const list = debtors ?? [];
  const totalDebt = list.reduce((s, d) => s + d.totalDebt, 0);
  const totalOverdue = list.reduce((s, d) => s + d.overdueDebt, 0);

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    const fd = new FormData(e.currentTarget);
    const userId = String(fd.get("userId") || "").trim();
    const packageName = String(fd.get("packageName") || "").trim();
    const totalAmount = Number(fd.get("totalAmount") || 0);
    const installmentCount = Number(fd.get("installmentCount") || 0);
    const firstDueDate = String(fd.get("firstDueDate") || "").trim();

    if (!userId || !packageName || totalAmount <= 0 || installmentCount < 1 || !firstDueDate) {
      setFormError("Lütfen tüm alanları eksiksiz ve geçerli doldurun.");
      return;
    }

    setBusy(true);
    try {
      const ok = await adminCreateInstallments({
        userId,
        packageId: "",
        packageName,
        totalAmount,
        installmentCount,
        firstDueDate: new Date(firstDueDate).toISOString(),
      });
      if (!ok) throw new Error("Taksit planı oluşturulamadı.");
      setShowCreate(false);
      e.currentTarget.reset();
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Taksit planı oluşturulurken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async (paymentId: string) => {
    setPayingId(paymentId);
    try {
      await adminMarkInstallmentPaid(paymentId);
      reload();
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Borçlular ve Taksitlendirme"
          description="Ödenmemiş taksitleri ve borçları görüntüle, taksit planı oluştur ve tahsilatı işaretle."
        />
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          Yeni Taksit Planı
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Borçlu öğrenci</p>
          <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">{list.length}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Toplam alacak</p>
          <p className="mt-2 font-heading font-bold text-2xl text-secondary-700">{formatPrice(totalDebt)}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Geciken tutar</p>
          <p className="mt-2 font-heading font-bold text-2xl text-red-600">{formatPrice(totalOverdue)}</p>
        </div>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-wallet-3-line"
          title="Borçlu öğrenci yok"
          description="Tüm ödemeler tahsil edilmiş. Yeni taksit planı oluşturduğunda burada listelenecek."
        />
      ) : (
        <div className="space-y-4">
          {list.map((d) => (
            <div key={d.userId} className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-background-200 bg-background-100/50">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                    <i className="ri-user-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="font-semibold text-foreground-950">{d.userName}</p>
                    <p className="text-xs text-foreground-500">
                      {d.pendingCount} bekleyen taksit
                      {d.overdueDebt > 0 && (
                        <span className="ml-2 text-red-600 font-medium">· {formatPrice(d.overdueDebt)} gecikmiş</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xs font-medium text-foreground-500">Toplam borç</p>
                  <p className="font-heading font-bold text-lg text-secondary-700">{formatPrice(d.totalDebt)}</p>
                </div>
              </div>

              <ul className="divide-y divide-background-200">
                {d.installments.map((inst) => {
                  const isOverdue = !!inst.dueDate && new Date(inst.dueDate).getTime() < Date.now();
                  return (
                    <li key={inst.id} className="flex items-center justify-between gap-3 px-5 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {inst.packageName || "Paket"}
                          {inst.installmentNumber && inst.installmentTotal ? (
                            <span className="ml-2 text-xs text-foreground-500">
                              Taksit {inst.installmentNumber}/{inst.installmentTotal}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {inst.dueDate ? `Vade: ${inst.dueDate}` : inst.createdAt}
                          {isOverdue && (
                            <span className="ml-2 inline-flex items-center gap-1 text-red-600 font-medium">
                              <i className="ri-alert-line" aria-hidden="true"></i>
                              Gecikmiş
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                          {formatPrice(inst.amount)}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleMarkPaid(inst.id)}
                          disabled={payingId === inst.id}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
                        >
                          {payingId === inst.id ? (
                            <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                          ) : (
                            <i className="ri-check-line" aria-hidden="true"></i>
                          )}
                          Ödendi
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Yeni Taksit Planı"
        titleId="create-installment-title"
        id="create-installment-modal"
        maxWidthClass="max-w-md"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label htmlFor="install-user" className={labelClass}>Öğrenci</label>
            <select id="install-user" name="userId" className={inputClass}>
              <option value="">Seçin...</option>
              {(students.data ?? []).map((s) => (
                <option key={s.userId} value={s.userId}>
                  {s.name}{s.grade ? ` · ${s.grade}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="install-package" className={labelClass}>Paket Adı</label>
            <input id="install-package" name="packageName" type="text" className={inputClass} placeholder="Örn: YKS Full Paket" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="install-amount" className={labelClass}>Toplam Tutar (₺)</label>
              <input id="install-amount" name="totalAmount" type="number" min={1} step="0.01" className={inputClass} placeholder="0.00" />
            </div>
            <div>
              <label htmlFor="install-count" className={labelClass}>Taksit Sayısı</label>
              <input id="install-count" name="installmentCount" type="number" min={1} max={24} className={inputClass} placeholder="Örn: 6" />
            </div>
          </div>
          <div>
            <label htmlFor="install-due" className={labelClass}>İlk Vade Tarihi</label>
            <input id="install-due" name="firstDueDate" type="date" className={inputClass} />
          </div>

          {formError && (
            <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Oluşturuluyor..." : "Taksitleri Oluştur"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}